import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Colors from '../colors';
import EncourageCreation from '../components/EncourageCreation';
import FormsFilters from '../components/FormsFilters';
import LoadingSpinner from '../components/LoadingSpinner';
import PlusIconButton from '../components/PlusIconButton';
import SearchBar from '../components/SearchBar';
import SelectableCard from '../components/SelectableCard';
import SelectableCardContentRow from '../components/SelectableCardContentRow';
import TailgateFormsAccordion from '../components/TailgateFormsAccordion';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import { createNewDocInDb, getAllDocsInCollection, getDocsByCollectionAndFK, getUserByUid } from "../firebase";
import { convertTimestampToFormattedString, convertTimestampToTime } from '../utils/helpers';

function TailgateForms({ user, isAdmin }) {
    const [forms, setForms] = useState([]);
    const [containerData, setContainerData] = useState([]);
    const [sortedDates, setSortedDates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredForms, setFilteredForms] = useState(forms);
    const [search, setSearch] = useState('');
    const [incidentFilters, setIncidentFilters] = useState([]);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [formCreatorFilter, setFormCreatorFilter] = useState('current-user');
    const [createNewFormModalVisible, setCreateNewFormModalVisible] = useState(false);
    const [formAccordions, setFormAccordions] = useState([]);
    const [loadedAllForms, setLoadedAllForms] = useState(false);

    const navigate = useNavigate();

    const handleOnClick = (formId) => {
        const form = forms.find(f => f.id === formId);
        navigate(`/forms/${formId}`, { state: { formData: form } });
    };

    const handleNewFormOnClick = async () => {
        setIsLoading(true);
        const userDoc = await getUserByUid(user.uid);
        const timestamp = Date();

        // DB schema for a RENR Safety Tailgate Form
        const emptyForm = {
            userId: user.uid,
            team: { id: '', name: '', userId: user.uid },
            locationName: '',
            instructors: [`${userDoc.first_name} ${userDoc.last_name}`],
            timestamp: timestamp,
            date: convertTimestampToFormattedString(timestamp),
            time: convertTimestampToTime(timestamp),
            notes: '',
            hazards: [],
            controlMeasures: [],
            firstAidLocation: '',
            designatedFirstAiders: '',
            musterPoint: '',
            nearestHospital: { name: '', address: '' },
            searchAndRescue: { name: '', phone: '' },
            localGovContact: { name: '', phone: '' },
            siteContact: { name: '', phone: '' },
            nearMissOccurred: false,
            incidentOccurred: false,
            nearMissDescription: '',
            isArchived: false,
        };
        const form = await createNewDocInDb('Forms', emptyForm);
        setIsLoading(false);

        navigate(`/forms/${form.id}`, { state: { formData: form } });
    };

    // Fetches only the current user's forms
    useEffect(() => {
        if (!user) return;
        const fetchForms = async () => {
            setIsLoading(true);
            const userId = user.uid;
            const formDocs = await getDocsByCollectionAndFK("Forms", 'userId', userId);
            setForms(formDocs);
            setIsLoading(false);
        };
        fetchForms();
    }, [user]);

    // Fetches all forms
    useEffect(() => {
        const fetchAllForms = async () => {
            if (formCreatorFilter === 'all-users' && !loadedAllForms) {
                setIsLoading(true);
                const formDocs = await getAllDocsInCollection("Forms");
                setForms(formDocs);
                setLoadedAllForms(true);
                setIsLoading(false);
            };
        };
        fetchAllForms();
        // eslint-disable-next-line
    }, [formCreatorFilter]);

    useEffect(() => {
        if (!user) return;
        const filtered = forms.filter((form) => {
            // Search bar
            const locationMatch = form?.locationName?.toLowerCase().includes(search.toLowerCase());
            const instructorMatch = form?.instructors?.some((instructor) => instructor.toLowerCase().includes(search.toLowerCase()));

            // Near misses / Incidents filters
            var includesNearMiss = true;
            var includesIncident = true;
            if (incidentFilters.includes('Near Miss')) {
                includesNearMiss = form.nearMissOccurred;
            }
            if (incidentFilters.includes('Incident')) {
                includesIncident = form.incidentOccurred;
            }

            // Date filters
            var afterStartDate = true;
            var beforeEndDate = true;
            const formDate = new Date(form.date);
            if (filterStartDate) {
                const startDate = new Date(filterStartDate);
                afterStartDate = startDate <= formDate;
            }
            if (filterEndDate.length > 0) {
                const endDate = new Date(filterEndDate);
                beforeEndDate = endDate >= formDate;
            }

            var formCreator = true;
            if (formCreatorFilter === 'current-user') {
                formCreator = form?.userId?.includes(user.uid);
            }

            // Combine it all
            return (locationMatch || instructorMatch)
                && includesNearMiss
                && includesIncident
                && afterStartDate
                && beforeEndDate
                && formCreator;
        });
        setFilteredForms(filtered);
        // eslint-disable-next-line
    }, [forms, search, incidentFilters, filterStartDate, filterEndDate, formCreatorFilter, user]);

    useEffect(() => {
        const containersDataArray = [];
        filteredForms.forEach(data => {
            const id = data.id;
            const timestamp = data.timestamp;
            const date = data.date;
            const instructorsString = data.instructors?.join(', ');
            const location = data.locationName;
            const team = data.team;

            const isArchived = data.isArchived;
            const statusIcon = isArchived
                ? <CheckCircleOutlineIcon sx={{ color: `${Colors.green}`, height: '30px', width: '30px' }} />
                : <AccessTimeIcon sx={{ color: Colors.yellow, height: '30px', width: '30px' }} />

            const statusString = data.isArchived
                ? 'ARCHIVED'
                : 'IN PROGRESS';

            // This object provides the data necessary to be
            // displayed in the Form's info card.
            const newObj = {
                isArchived: isArchived,
                userId: data.userId,
                id: id,
                date: date,
                instructors: instructorsString,
                location: location,
                timestamp: timestamp,
                infoRows:
                    [
                        <SelectableCardContentRow
                            key={'STATUS'}
                            iconComponent={statusIcon}
                            title={'STATUS'}
                            mainText={statusString} />,
                        <SelectableCardContentRow
                            key={'LOCATION'}
                            iconComponent={<LocationOnIcon sx={{ color: Colors.blue, height: '30px', width: '30px' }} />}
                            title={'LOCATION'}
                            mainText={location} />,
                        <SelectableCardContentRow
                            key={'INSTRUCTORS'}
                            iconComponent={<SupervisorAccountIcon sx={{ color: Colors.black, height: '30px', width: '30px' }} />}
                            title={'INSTRUCTORS'}
                            mainText={instructorsString} />,
                        <SelectableCardContentRow
                            key={'TEAM'}
                            iconComponent={<GroupsIcon sx={{ color: Colors.green, height: '30px', width: '30px' }} />}
                            title={'TEAM'}
                            mainText={team.name}
                            subText={''} />
                    ]
            }
            containersDataArray.push(newObj);
        });

        // Group the formatted objects to be display by date
        const groupedByDate = containersDataArray.reduce((acc, curr) => {
            const date = curr.date;
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(curr);
            return acc;
        }, {});

        // Sort the dates that were found
        const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);
            return dateB.valueOf() - dateA.valueOf();
        });
        setSortedDates(sortedDates);
        setContainerData(groupedByDate);
    }, [filteredForms]);

    useEffect(() => {
        function renderTailgateFormsAccordion(dates, containerData) {
            return dates && dates.map((date, index) => {
                const containersData = containerData[date];
                const infoContainers = containersData?.map((container) => {
                    return (
                        <SelectableCard
                            key={container.id}
                            infoRows={container.infoRows}
                            onClickHandler={() => handleOnClick(container.id)}
                            showEditIcon={container.userId === user.uid && !container.isArchived}
                        />
                    );
                });
                const numCards = infoContainers?.length;
                return (
                    <TailgateFormsAccordion
                        key={date}
                        date={date}
                        summaryText={`(${numCards}) Forms`}
                        infoContainers={infoContainers}
                        initialExpanded={false} />
                );
            });
        };

        const renderedAccordions = renderTailgateFormsAccordion(sortedDates, containerData);
        setFormAccordions(renderedAccordions);
        // eslint-disable-next-line
    }, [containerData]);

    return (
        <div>
            {/* Loading Spinner during async functions */}
            {isLoading && <LoadingSpinner isLoading={isLoading} />}

            {/* Search + Filter */}
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <SearchBar placeholder={'Search by Instructor or Location'} search={search} setSearch={setSearch} />
                <FormsFilters setIncidentFilters={setIncidentFilters} setStartDateFilter={setFilterStartDate} setEndDateFilter={setFilterEndDate} setFormCreatorFilter={setFormCreatorFilter} isAdmin={isAdmin} />
            </div>

            {/* If no Forms to display */}
            {(forms.length === 0) &&
                <EncourageCreation
                    headerText={'(0) Forms Found'}
                    suggestionText={'Tap the plus icon to create a new form.'} />}

            {(forms.length > 0 && filteredForms.length === 0 &&
                <div style={{ textAlign: 'center', marginTop: '15px' }}>No forms with current filter parameters. Change filters to see forms.</div>)}

            {/* Render Retrieved Forms */}
            {(filteredForms.length > 0) && formAccordions}

            {/* Create New Form Button */}
            <Box sx={{ position: 'fixed', bottom: '20px', right: '20px' }} >
                <PlusIconButton color={Colors.green} onClick={() => setCreateNewFormModalVisible(true)} />
            </Box>

            {/* Create New Form Confirmation Modal */}
            <ConfirmationModal
                isVisible={createNewFormModalVisible}
                setIsVisible={setCreateNewFormModalVisible}
                title={'Create a new Safety Tailgate Meeting form?'}
                mainAction={'Create'}
                mainActionHandler={handleNewFormOnClick}
                color={Colors.green} />
        </div >
    )
};

export default TailgateForms;